export type EmailContentFormat = 'html' | 'text';

export type TemplatePreviewInput = {
  template: string;
  contentFormat?: EmailContentFormat;
  sampleRow?: Record<string, unknown>;
  emailColumn?: string;
  nameColumn?: string;
  customFields?: Record<string, string>;
  siteLabel?: string;
  siteUrl?: string;
  inviteRole?: string;
  inviteCampaign?: string;
  sampleEmail?: string;
};

const DEFAULT_SAMPLE_ROW: Record<string, unknown> = {
  name: 'John Doe',
  email: 'john.doe@example.com',
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function resolveSiteBranding(site?: {
  label?: string | null;
  domain?: string | null;
  frontendUrl?: string | null;
} | null): { siteLabel: string; siteUrl: string } {
  const siteLabel = site?.label?.trim() || 'Your Brand';
  const fromFrontend = site?.frontendUrl?.trim().replace(/\/$/, '');
  if (fromFrontend) {
    return { siteLabel, siteUrl: fromFrontend };
  }
  const domain = site?.domain?.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
  return {
    siteLabel,
    siteUrl: domain ? `https://${domain}` : 'https://example.com',
  };
}

export function buildRecipientGreeting(name: string, options?: { html?: boolean }): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed === 'Valued Customer') {
    return 'Dear Partner,';
  }
  const display = options?.html === false ? trimmed : escapeHtml(trimmed);
  return `Dear ${display},`;
}

export function buildPreviewInviteUrl(
  email: string,
  options?: {
    role?: string;
    campaign?: string;
    baseUrl?: string;
  }
): string {
  const base = (options?.baseUrl || 'https://example.com').replace(/\/$/, '');
  const role = options?.role?.trim() || 'travel_agent';
  const params = new URLSearchParams({
    utm_source: 'email',
    utm_medium: 'email',
    utm_campaign: options?.campaign?.trim() || 'travel_agent_invite',
    ref: 'email',
    role,
  });
  const trimmedEmail = email.trim();
  if (trimmedEmail) {
    params.set('email', trimmedEmail);
  }
  return `${base}/login?${params.toString()}`;
}

function isUserCustomFieldKey(fieldName: string): boolean {
  return fieldName.length > 0 && !fieldName.startsWith('__');
}

function personalizePreviewContent(input: TemplatePreviewInput): string {
  const template = input.template;
  if (!template.trim()) {
    return '';
  }

  const contentFormat = input.contentFormat ?? 'html';
  const useHtml = contentFormat === 'html';
  const formatValue = (value: string) => (useHtml ? escapeHtml(value) : value);

  const row = input.sampleRow ?? DEFAULT_SAMPLE_ROW;
  const emailColumn = input.emailColumn || 'email';
  const email = String(
    row[emailColumn] ?? row.email ?? input.sampleEmail ?? DEFAULT_SAMPLE_ROW.email
  ).trim();
  const name = input.nameColumn
    ? String(row[input.nameColumn] || '').trim()
    : String(row.name || row.Name || 'John Doe').trim() || 'John Doe';

  const siteLabel = input.siteLabel?.trim() || 'Your Brand';
  const siteUrl = input.siteUrl?.trim() || 'https://example.com';

  let content = template;
  const recipientGreeting = buildRecipientGreeting(name, { html: useHtml });

  content = content.replace(/\{\{name\}\}/gi, formatValue(name));
  content = content.replace(/\{\{RECIPIENT_GREETING\}\}/gi, recipientGreeting);
  content = content.replace(/\{\{RECIPIENT_NAME\}\}/gi, formatValue(name));
  content = content.replace(/\{\{SITE_NAME\}\}/gi, formatValue(siteLabel));
  content = content.replace(/\{\{SITE_URL\}\}/gi, formatValue(siteUrl));

  if (input.customFields) {
    Object.keys(input.customFields).forEach((fieldName) => {
      if (!isUserCustomFieldKey(fieldName)) return;
      const col = input.customFields![fieldName];
      const fieldValue = row[col] ?? '';
      const regex = new RegExp(`\\{\\{${fieldName}\\}\\}`, 'gi');
      content = content.replace(regex, String(fieldValue));
    });
  }

  Object.keys(row).forEach((key) => {
    const value = String(row[key] ?? '');
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'gi');
    content = content.replace(regex, value);
  });

  if (/\{\{CTA_URL\}\}/i.test(content)) {
    const ctaUrl = buildPreviewInviteUrl(email, {
      role: input.inviteRole,
      campaign: input.inviteCampaign,
      baseUrl: siteUrl,
    });
    content = content.replace(/\{\{CTA_URL\}\}/gi, formatValue(ctaUrl));
  }

  return content;
}

/** Builds personalized HTML for template preview (mirrors backend send personalization). */
export function buildTemplatePreviewHtml(input: TemplatePreviewInput): string {
  return personalizePreviewContent({ ...input, contentFormat: 'html' });
}

/** Builds personalized plain-text preview. */
export function buildTemplatePreviewText(input: TemplatePreviewInput): string {
  return personalizePreviewContent({ ...input, contentFormat: 'text' });
}

export function buildTemplatePreview(input: TemplatePreviewInput): {
  contentFormat: EmailContentFormat;
  body: string;
} {
  const contentFormat = input.contentFormat ?? 'html';
  return {
    contentFormat,
    body: personalizePreviewContent({ ...input, contentFormat }),
  };
}
